/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { useTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import TogglableContent from 'bundles/page/components/TogglableContent';
import CML from 'bundles/unified-description-page-common/components/shared/CML';
import ToggleButtonForTogglableContent from 'bundles/unified-description-page-common/components/shared/ToggleButtonForTogglableContent';
import ToggleButtonForTogglableContentBackground from 'bundles/unified-description-page-common/components/shared/ToggleButtonForTogglableContentBackground';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

const COLLAPSED_HEIGHT = 78;
const NARROW_COLLAPSED_HEIGHT = 192;
const NARROW_EXTENDED_COLLAPSED_HEIGHT = 324;

export function getCollapsedHeight({
  useNarrowLayout,
  useExtendedHeight,
}: {
  useNarrowLayout: boolean;
  useExtendedHeight: boolean;
}) {
  if (!useNarrowLayout) {
    return COLLAPSED_HEIGHT;
  }

  if (useExtendedHeight) {
    return NARROW_EXTENDED_COLLAPSED_HEIGHT;
  }

  return NARROW_COLLAPSED_HEIGHT;
}

const styles = {
  container: css`
    white-space: pre-wrap;

    a {
      display: inline;
    }

    p {
      margin-top: 0;
    }
  `,
  overrideStyles: css`
    .toggle-button-wrapper {
      position: relative;
    }

    .toggle-button-wrapper span {
      color: var(--cds-color-interactive-primary);
    }
  `,
};

type Props = {
  productId?: string;
  description?: string | null;
  cmlDescription?: CmlContent | null;
  trackingName?: string;
  ariaHidden?: boolean;
  collapsedHeight?: number;
  enableGradient?: boolean;
  trackingData?: EventData<'expand_accordion' | 'collapse_accordion'>;
};

const TogglableDescription = ({
  productId,
  description,
  cmlDescription,
  trackingName,
  trackingData,
  ariaHidden,
  collapsedHeight = COLLAPSED_HEIGHT,
  enableGradient,
}: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  if (!description && !cmlDescription) {
    return null;
  }

  const handleToggleClick = trackingData
    ? ({ collapsed }: { collapsed: boolean }) => {
        if (collapsed) {
          track('expand_accordion', { ...trackingData, ...eventingProductData });
        } else {
          track('collapse_accordion', { ...trackingData, ...eventingProductData });
        }
      }
    : undefined;

  return (
    <div css={[styles.container, styles.overrideStyles]}>
      <TogglableContent
        id="syllabus-description-content"
        tabIndex={0}
        ariaHidden={ariaHidden}
        childrenWrapperTag="div"
        className="about-section"
        collapsedHeight={collapsedHeight}
        disableCollapseScroll
        renderToggleButtonContentFunction={
          enableGradient && enableXDPRedesign
            ? ToggleButtonForTogglableContentBackground
            : ToggleButtonForTogglableContent
        }
        toggleButtonClassName="bold body-2-text"
        toggleButtonType="button"
        useSmartCollapse
        trackingName={trackingName ?? 'description'}
        {...(productId ? { trackingData: { courseId: productId } } : {})}
        onToggleClick={handleToggleClick}
      >
        {cmlDescription ? <CML cml={cmlDescription} /> : description}
      </TogglableContent>
    </div>
  );
};

export default TogglableDescription;
